import { NPC_CONFIG } from '../../../shared/content/generated/entities.gen';
import { QUEST_DATABASE } from '../../../shared/content/generated/quests.gen';
import { ZONE_CONFIGS } from '../../../shared/content/generated/zones.gen';
import { missingQuestPrerequisites } from '../../../shared/quests/questChain';
import { isQuestOffered } from '../../../shared/quests/questOffer';

export interface NpcOfferPlayerState {
  level: number;
  activeQuestIds: ReadonlySet<string>;
  completedQuestIds: ReadonlySet<string>;
}

export interface NpcQuestOffers {
  /** Quests this NPC would offer the player now (the '!' above them). */
  available: string[];
  /** A featured quest the NPC holds back for a level or an earlier quest. */
  hasLockedFeatured: boolean;
}

/**
 * What an NPC would offer the player now, by NPCInteractionHandler's rules: offered at all, not
 * active, not done unless repeatable, level met, earlier quests done. A projection for markers and
 * hints; the server alone offers and accepts.
 */
export function npcQuestOffers(questIds: readonly string[] | undefined, state: NpcOfferPlayerState): NpcQuestOffers {
  let hasLockedFeatured = false;
  const available = (questIds ?? []).filter((questId) => {
    const quest = QUEST_DATABASE[questId];
    if (!quest || state.activeQuestIds.has(questId)) return false;
    if (!isQuestOffered(quest)) return false; // plan 24 D28
    if (!quest.repeatable && state.completedQuestIds.has(questId)) return false;
    const isLocked = quest.requiredLevel > state.level ||
      missingQuestPrerequisites(quest, id => state.completedQuestIds.has(id)).length > 0;
    if (isLocked) {
      if (quest.featured) hasLockedFeatured = true;
      return false;
    }
    return true;
  });
  return { available, hasLockedFeatured };
}

export interface NearbyQuestGiver { npcId: string; name: string; featured: boolean }

/**
 * The NPCs standing on `mapId` who would offer the player a quest now: the ones already wearing a
 * '!' there, so naming them reveals nothing the world does not show.
 */
export function nearbyQuestGivers(mapId: string | null | undefined, state: NpcOfferPlayerState): NearbyQuestGiver[] {
  const spawns = mapId ? ZONE_CONFIGS[mapId]?.npcSpawns ?? [] : [];
  const seen = new Set<string>();
  const givers: NearbyQuestGiver[] = [];
  for (const { npcId } of spawns) {
    const npc = NPC_CONFIG[npcId];
    if (!npc || seen.has(npcId)) continue;
    seen.add(npcId);
    const { available } = npcQuestOffers(npc.availableQuests, state);
    if (available.length) givers.push({ npcId, name: npc.name, featured: available.some(id => QUEST_DATABASE[id]?.featured) });
  }
  return givers;
}
